import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import GraduateModal from 'Components/Graduate/GraduateModal';
import { useClassroomInterruption } from 'Util/Hooks';
import { useVideoPlayer } from 'Context/VideoPlayer';
import { useOnlineCoursesData, useMyCourseData } from 'Domain/MyCourseData';
import { useConfig } from 'Context/ConfigContext';
import { useCapacitor } from 'Util/Capacitors';
import { useClassroomCourseData } from 'Domain/Classroom';
import {
  sendDataToGTM,
  GTM_EVENT_NAME,
  GTM_DATA_LAYER_PROPERTY,
  GTM_SOCIAL_CHANNEL_NAME,
} from 'Util/Analytics';

import usePersonalizedDiscountCode from '../Domain/usePersonalizedDiscoundCode';
import useGeneratePersonalizedDiscountCode from '../Domain/useGeneratePersonalizedDiscountCode';
import useShowGraduateModal from '../Domain/useShowGraduateModal';

const getShouldShowSectionByError = (error) => {
  if (error) {
    if (error.message.includes('Not found personalized discount code template')) {
      return false;
    }

    if (error.message.includes('This classroom has incorrect conditions')) {
      return false;
    }
  }
  return true;
};

const CourseGraduateModal = ({
  isSwitchedToComplete,
  isGraduateModalOpen,
  onOpenGraduateModal,
  onCloseGraduateModal,
}) => {
  const { onlineCourseDetailUrl } = useConfig();
  const { courseCode } = useMyCourseData();
  const { data: onlineCourseData } = useOnlineCoursesData({ courseCode: courseCode });
  const { data: classroomCourseData } = useClassroomCourseData();
  const { videoState, VIDEO_STATE, player } = useVideoPlayer();
  const { openUrl } = useCapacitor();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { data: personalizedDiscountData, error: personalizedDiscountError } =
    usePersonalizedDiscountCode(classroomCourseData?.classroomCourse?.id);
  const [isGeneratingPersonalizedDiscountCode, setIsGeneratingPersonalizedDiscountCode] =
    useState(true);
  const onGeneratingComplete = useCallback(() => {
    // Delay for 2 second while generating discount
    setTimeout(() => setIsGeneratingPersonalizedDiscountCode(false), 2000);
  }, []);
  const [generatePersonalizedDiscountCode, { error: generatePersonalizedDiscountCodeError }] =
    useGeneratePersonalizedDiscountCode(classroomCourseData?.classroomCourse?.id, {
      onComplete: onGeneratingComplete,
      onError: onGeneratingComplete,
    });
  const {
    courseData: { progressPercent },
  } = useClassroomInterruption();
  const [shouldShowGraduateModal, updateShouldShowGraduateModal] = useShowGraduateModal({
    courseCode,
  });
  const shouldShowPersonalizedDiscountSection = useMemo(
    () => getShouldShowSectionByError(generatePersonalizedDiscountCodeError),
    [generatePersonalizedDiscountCodeError]
  );

  const logGTMEvent = (socialChannelName) => {
    const dataLayerDetail = {
      event: GTM_EVENT_NAME.SOCIAL_SHARE_BUTTON_CLICK,
      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: onlineCourseData?.title,
      [GTM_DATA_LAYER_PROPERTY.SOCIAL_CHANNEL]: socialChannelName,
    };

    sendDataToGTM(dataLayerDetail);
  };

  const shareToLinkedIn = () => {
    const width = 950;
    const height = 520;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 4;
    const windowOptions = `width=${width},height=${height},left=${left},top=${top}`;
    const sharedUrl = `https://www.linkedin.com/sharing/share-offsite/?url=
      ${onlineCourseDetailUrl}/${onlineCourseData?.permalink}`;
    openUrl(sharedUrl, { windowOptions });

    // logging GTM event
    logGTMEvent(GTM_SOCIAL_CHANNEL_NAME.LINKEDIN);
  };

  const shareToTwitter = (initialText) => {
    const width = 550;
    const height = 450;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 4;
    const windowOptions = `width=${width},height=${height},left=${left},top=${top}`;
    const sharedUrl = `http://twitter.com/share?url=${encodeURIComponent(
      `${onlineCourseDetailUrl}/${onlineCourseData?.permalink}`
    )}&text=${encodeURIComponent(initialText)}`;
    openUrl(sharedUrl, { windowOptions });

    // logging GTM event
    logGTMEvent(GTM_SOCIAL_CHANNEL_NAME.TWITTER);
  };

  const shareToFacebook = () => {
    const width = 550;
    const height = 450;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 4;
    const windowOptions = `width=${width},height=${height},left=${left},top=${top}`;
    const sharedUrl = `http://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      `${onlineCourseDetailUrl}/${onlineCourseData?.permalink}`
    )}`;
    openUrl(sharedUrl, { windowOptions });

    // logging GTM event
    logGTMEvent(GTM_SOCIAL_CHANNEL_NAME.FACEBOOK);
  };

  const onCopyButtonClick = () => {
    // Open `copied` tooltip for 1.5 second and close it
    navigator.clipboard.writeText(personalizedDiscountData?.code ?? '');
    setIsTooltipOpen(true);
    setTimeout(() => {
      setIsTooltipOpen(false);
    }, 1500);
  };

  useEffect(() => {
    const shouldShowAt100Percent = progressPercent === 100;
    const shouldShowOnSubsectionComplete =
      (player !== null && videoState === VIDEO_STATE.ENDED) ||
      (player === null && isSwitchedToComplete);
    if (shouldShowOnSubsectionComplete && shouldShowAt100Percent && shouldShowGraduateModal) {
      // Open graduate modal
      onOpenGraduateModal();
      // Shouldn't show graduate modal in next time
      updateShouldShowGraduateModal();
    }
  }, [progressPercent, videoState, player, isSwitchedToComplete, shouldShowGraduateModal]);

  useEffect(() => {
    if (isGraduateModalOpen && personalizedDiscountError) {
      generatePersonalizedDiscountCode();
    }
  }, [isGraduateModalOpen, personalizedDiscountError]);

  return (
    <GraduateModal
      isActive={isGraduateModalOpen}
      isTooltipOpen={isTooltipOpen}
      isGeneratingPersonalizedDiscountCodeLoading={isGeneratingPersonalizedDiscountCode}
      shouldShowPersonalizedDiscountSection={shouldShowPersonalizedDiscountSection}
      courseTitle={onlineCourseData?.title}
      personalizedDiscount={personalizedDiscountData}
      onClose={onCloseGraduateModal}
      onFacebookShare={shareToFacebook}
      onTwitterShare={() =>
        shareToTwitter(`คอร์สออนไลน์ดีบอกต่อ เรียน ${onlineCourseData?.title} จบแล้ว! #Skooldio`)
      }
      onLinkedinShare={shareToLinkedIn}
      onCopyButtonClick={onCopyButtonClick}
    />
  );
};

CourseGraduateModal.propTypes = {
  isSwitchedToComplete: PropTypes.bool,
  isGraduateModalOpen: PropTypes.bool,
  onOpenGraduateModal: PropTypes.func,
  onCloseGraduateModal: PropTypes.func,
};

CourseGraduateModal.defaultProps = {
  isSwitchedToComplete: false,
  onOpenGraduateModal: () => {},
  onCloseGraduateModal: () => {},
};

export default CourseGraduateModal;
